<script lang="ts" setup>
import { mapProductsAlgolia, ProductAlgolia } from "~/utils/mapProducts"
import { checkEnvSite } from "~~/server/utils/getAlgoliaConfig"
import { buildExcludeFilter } from "~/utils/algolia"
import { ComponentAlgoliaListingProps } from "./ComponentAlgoliaListing.props"
import {
  VIEW_ITEM_LIST_IDS,
  VIEW_ITEM_LIST_NAMES
} from "~/utils/analytics/viewItemList"

const props = defineProps<ComponentAlgoliaListingProps>()
const { fullPath } = useRoute()
const indexName: string = `TOP_PRODUCTS${checkEnvSite()}`

const getGA4Name = () => {
  if (props.title) {
    return props.title
  }
  return fullPath
}

const formatProducts = (items: ProductAlgolia[]) => {
  return mapProductsAlgolia(items)
}

const sortingItems = ref([
  {
    value: `TOP_PRODUCTS${checkEnvSite()}`,
    label: "Miglior risultato"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite()}_discount-desc`,
    label: "Sconto decrescente"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite()}_name-asc`,
    label: "Nome prodotto A-Z"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite()}_name-desc`,
    label: "Nome prodotto Z-A"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite()}_price-asc`,
    label: "Prezzo crescente"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite()}_price-desc`,
    label: "Prezzo decrescente"
  }
])

const client = useAlgoliaRef()
const stateMapping = useStateMapping()
const algoliaRouter = useCustomRouting()

const configuration = ref({
  indexName: indexName,
  insights: true,
  routing: {
    router: algoliaRouter.value.router,
    stateMapping: stateMapping(indexName)
  },
  searchClient: client
})

const { getAlgoliaAnalyticsTags } = useAlgoliaAnalytics()

const widgets = ref([
  useAisHits({
    // @ts-ignore
    transformItems: (items) => formatProducts(items)
  }),
  useAisConfigure({
    // `page` intentionally NOT set here — pagination is owned by IS via the
    // hidden AisPagination widget below. See ListingPage.vue for details.
    searchParameters: {
      query: "",
      hitsPerPage: props.pagination,
      distinct: true,
      clickAnalytics: true,
      filters: props.facet,
      ...getAlgoliaAnalyticsTags(),
      ...buildExcludeFilter({ excludeOutOfMarket: true })
    }
  }),
  // Hidden pagination widget: lets IS own the `page` state (see ListingPage.vue).
  useAisPagination({}),
  useAisSearchBox({}),
  useAisStats({}),
  useAisSortBy({
    items: sortingItems.value
  }),
  useAisClearRefinements({}, "all"),
  useAisClearRefinements(
    {
      includedAttributes: ["price.amount_float"]
    },
    "price.amount_float"
  ),
  useAisClearRefinements(
    {
      includedAttributes: ["brand.name"]
    },
    "brand.name"
  ),
  useAisClearRefinements(
    {
      includedAttributes: ["mainCategory.name"]
    },
    "mainCategory.name"
  ),
  useAisClearRefinements(
    {
      includedAttributes: ["inStock", "deductibleProduct"]
    },
    "availability"
  ),
  useAisRefinementList({
    attribute: "brand.name"
  }),
  useAisCurrentRefinements({}, "all"),
  useAisCurrentRefinements(
    {
      includedAttributes: ["brand.name"]
    },
    "brand.name"
  ),
  useAisCurrentRefinements(
    {
      includedAttributes: ["mainCategory.name"]
    },
    "mainCategory.name"
  ),
  useAisRangeInput({
    attribute: "price.amount_float"
  }),
  useAisRefinementList({
    attribute: "mainCategory.name"
  }),
  useAisToggleRefinement({
    attribute: "isDiscounted"
  }),
  useAisToggleRefinement({
    attribute: "inStock"
  }),
  useAisToggleRefinement({
    attribute: "deductibleProduct"
  })
])
</script>

<template>
  <div class="grid-content-algolia">
    <AisInstantSearch
      v-if="widgets && configuration"
      :widgets="widgets"
      :configuration="configuration"
    >
      <div class="grid-standard mt-4 md:pt-6" id="algolia-plp-filters">
        <p
          v-if="title"
          class="elephant-bold col-span-full hidden md:mb-6 md:block"
        >
          {{ title }}
        </p>

        <AlgoliaPlpFilters
          :show-query="false"
          :hasResultsNumberUnderFilters="showResultsNumber && !title"
          class="component-algolia-listing-filters"
          :class="{
            'component-algolia-listing-filters-hidden': !showResultsNumber
          }"
        />
      </div>

      <AisHits>
        <template #default="{ items }">
          <GAListPageList
            :name="VIEW_ITEM_LIST_NAMES.PROMO"
            :id="VIEW_ITEM_LIST_IDS.ON_SALE"
          >
            <GridListCategory
              ref="aisComponent"
              :title="title"
              :products-list="items"
              :list-name="VIEW_ITEM_LIST_NAMES.PROMO"
              :list-id="VIEW_ITEM_LIST_IDS.ON_SALE"
              :max-pages="props.maxPages"
            />
          </GAListPageList>
        </template>
      </AisHits>
    </AisInstantSearch>
  </div>
</template>

<style scoped lang="scss">

:deep() {
  .component-algolia-listing-filters-hidden {
    p {
      &.mobile-filters-bar__results,
      &.number-of-results {
        @apply hidden;
      }
    }
  }
}
.grid-content-algolia {
  @apply col-span-full mx-[-16px] md:mx-[-71px] lg:mx-[-228px];
}

.cms-content__generic-page {
  .grid-content-algolia {
    @apply px-4 md:px-[71px] lg:px-[228px];
  }
}

</style>
